import { useMemo, useState } from 'react'

import { useAppConfirm } from '../../../base/contexts/AppConfirmProvider.tsx'
import { useError } from '../../../base/contexts/ErrorProvider.tsx'
import { useToast } from '../../../base/contexts/ToastContext.tsx'
import useAuth from '../../../base/hooks/useAuth.ts'
import { TipoArticuloInputProps, TipoArticuloProps } from '../../../interfaces/tipoArticulo.ts'
import { getEntidadInput } from '../../../utils/getEntidadInput.ts'
import { swalClose, swalLoading } from '../../../utils/swal.ts'
import { apiTipoArticuloEliminar } from '../api/apiTipoArticuloEliminar.ts'

type ActionState =
  | { mode: 'idle'; open: false; data: null }
  | { mode: 'registrar'; open: true; data: null }
  | { mode: 'actualizar'; open: true; data: TipoArticuloInputProps }
  | { mode: 'ordenar'; open: true; data: null }

const initialState: ActionState = { mode: 'idle', open: false, data: null }

/**
 * Operaciones de articulo
 */
export const useTipoArticulo = () => {
  const { user } = useAuth()
  const entidad = useMemo(() => getEntidadInput(user), [user])
  const [action, setAction] = useState<ActionState>(initialState)

  /** hooks auxiliares */
  const { requestConfirm } = useAppConfirm()
  const { toast } = useToast()
  const { showError } = useError()

  const onActionReset = () => setAction(initialState)
  const onActionRegistrar = () => setAction({ mode: 'registrar', open: true, data: null })
  const onActionOrdenar = () => setAction({ mode: 'ordenar', open: true, data: null })
  const onActionActualizar = (data: TipoArticuloProps) => {
    const nData: TipoArticuloInputProps = {
      codigo: data.codigo,
      descripcion: data.descripcion,
      orden: (data as any).orden || 0,
      grupoUnidadMedida: '',
      notas: (data.notas || []).map((d) => ({ key: d, value: d })),
    }
    setAction({ mode: 'actualizar', open: true, data: nData })
  }

  /** Me permite eliminar un registro */
  const onEliminar = async (row: TipoArticuloProps, onSuccess?: () => void) => {
    const { confirmed } = await requestConfirm({
      confirmButtonColor: 'error',
      title: 'Eliminar tipo articulo',
      description: `Se eliminará el tipo articulo ${row.codigo} - ${row.descripcion}?`,
      steps: ['No debe estar asociado con ningún articulo'],
    })
    if (!confirmed) return
    swalLoading()
    try {
      await apiTipoArticuloEliminar(row.codigo)
      toast.success('Registro eliminado correctamente')
      if (onSuccess) onSuccess()
    } catch (e) {
      showError(e)
    } finally {
      swalClose()
    }
  }

  return {
    user,
    entidad,
    sucursal: user.sucursal,
    pva: user.puntoVenta,
    onEliminar,
    onActionReset,
    onActionActualizar,
    onActionRegistrar,
    onActionOrdenar,
    action,
  }
}
